import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import AssetImage from "@/components/AssetImage";
import { useAsset } from "@/hooks/useAsset";
import { formatChallengeDateRange } from "@/utils/formatChallengeDateRange";
import FrameButton from "@/components/buttons/FrameButton";
import { isColorDarkOrLight } from "@/utils/isColorDarkOrLight";
import classNames from "classnames";
import { isMobile } from "react-device-detect";
import { sanitizeCssUrl } from "@/utils/urlSanitization";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import "./BrandChallenges.scss";

export function getCurrentEstTime() {
    return DateTime.now().setZone("America/New_York")
        .toMillis();
}

interface Challenge {
    id: string;
    name: string;
    cardTitle?: string;
    description: string;
    brandLogo?: string;
    brandColor: string;
    brandMapSlug?: string;
    backgroundImageLarge?: string;
    backgroundImageSmall?: string;
    start: number;
    end: number;
    isAccepted?: boolean;
}

interface BrandChallengesProps {
    challenges?: Challenge[];
    title?: string;
    isLoading?: boolean;
}

const BrandChallenges: React.FC<BrandChallengesProps> = ({
    challenges = [],
    title = "🏆 Active Missions",
    isLoading = false,
}) => {
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(0);
    const [currentDate, setCurrentDate] = useState(getCurrentEstTime());
    const [isPaused, setIsPaused] = useState(false);

    const displayChallenges = (challenges || []).slice(0, 3);
    const currentChallenge = displayChallenges[activeIndex];

    const { data } = useAsset(currentChallenge ? currentChallenge.backgroundImageSmall as any : null);

    useEffect(() => {
        const currentTimeIntervalKey = setInterval(() => setCurrentDate(getCurrentEstTime()), 1000 * 10);
        return () => clearInterval(currentTimeIntervalKey);
    }, []);

    // Auto-rotate carousel with stable reference
    useEffect(() => {
        if (displayChallenges.length <= 1 || isPaused) {
            return;
        }

        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % displayChallenges.length);
        }, 10000); // Slowed down to 10s

        return () => clearInterval(interval);
    }, [displayChallenges.length, isPaused]); // Re-run if paused state changes

    const handleChallengeClick = (challenge: Challenge) => {
        navigate(challenge.brandMapSlug ? `/brands/${challenge.brandMapSlug}` : `/challenges/${challenge.id}`);
    };

    const getChallengeLabel = (challenge: Challenge) => {
        if (challenge.end <= currentDate) {
            return "Debrief";
        }
        if (challenge.start > currentDate) {
            return "Scout";
        }
        return "Join";
    };

    const getUrgencyStatus = (challenge: Challenge) => {
        const hoursRemaining = (challenge.end - currentDate) / (1000 * 60 * 60);

        if (challenge.end <= currentDate) {
            return { status: "expired", label: "ENDED" };
        }
        if (hoursRemaining <= 24) {
            return { status: "urgent", label: "ENDING SOON" };
        }
        if (hoursRemaining <= 48) {
            return { status: "warning", label: "LIMITED TIME" };
        }
        if (challenge.isAccepted) {
            return { status: "active", label: "ACTIVE" };
        }
        return null;
    };

    const handleDotClick = (index: number) => {
        setActiveIndex(index);
        setIsPaused(true);
        // Resume auto-rotation after user interaction
        setTimeout(() => setIsPaused(false), 10000); // Pause for 10s after interaction
    };

    const handleNext = () => {
        setActiveIndex((prev) => (prev + 1) % displayChallenges.length);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 10000);
    };

    const handlePrev = () => {
        setActiveIndex((prev) => (prev - 1 + displayChallenges.length) % displayChallenges.length);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 10000);
    };

    if (isLoading) {
        return (
            <div className="brand-challenges brand-challenges--loading">
                <div className="brand-challenges__skeleton-title" />
                <div className="brand-challenges__skeleton-card" />
            </div>
        );
    }

    if (!displayChallenges.length) {
        return null;
    }

    return (
        <div className="brand-challenges">
            <div className="brand-challenges__header">
                <h3 className="brand-challenges__title">{title}</h3>
                {/* Navigation controls in header */}
                {displayChallenges.length > 1 &&
          <div className="brand-challenges__nav-controls">
              <button
                  className="challenge-nav challenge-nav--prev"
                  onClick={handlePrev}
                  aria-label="Previous challenge"
              >
                  <IoChevronBack />
              </button>
              <button
                  className="challenge-nav challenge-nav--next"
                  onClick={handleNext}
                  aria-label="Next challenge"
              >
                  <IoChevronForward />
              </button>
          </div>
                }
            </div>

            {currentChallenge &&
        <div
            className="overwolf-go-vip-challenges_card"
            style={{
                backgroundImage: data?.cdnUrl ? `url(${sanitizeCssUrl(data.cdnUrl)})` : undefined,
                cursor: "pointer",
            }}
            onClick={() => handleChallengeClick(currentChallenge)}
        >
            <div className="overwolf-go-vip-challenges_card_heading">
                <div className="overwolf-go-vip-challenges_card_heading_info">
                    <AssetImage assetId={currentChallenge.brandLogo as any} alt="brand-logo"/>
                    <h5>{currentChallenge.cardTitle || currentChallenge.name}</h5>
                    <span>{currentChallenge.start && currentChallenge.end ? formatChallengeDateRange(currentChallenge.start, currentChallenge.end) : "Coming Soon"}</span>
                </div>
                <FrameButton
                    size="narrow"
                    color="purple"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleChallengeClick(currentChallenge);
                    }}
                    forwardProps={{style: {backgroundColor: currentChallenge.brandColor}}}
                    className={classNames("overwolf-go-vip-challenges_card_heading_action", isColorDarkOrLight(currentChallenge.brandColor))}
                >
                    {getChallengeLabel(currentChallenge)}
                </FrameButton>
            </div>

            <div className="overwolf-go-vip-challenges_card_main">
                <span>{currentChallenge.description}</span>
            </div>

            {/* Pagination dots */}
            {displayChallenges.length > 1 &&
            <div className="challenge-pagination">
                {displayChallenges.map((_, index) =>
                    <button
                        key={index}
                        className={`dot ${index === activeIndex ? "dot--active" : ""}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDotClick(index);
                        }}
                        aria-label={`Go to challenge ${index + 1}`}
                    />)}
            </div>
            }
        </div>
            }
        </div>
    );
};

export default React.memo(BrandChallenges);